<template>
  <v-skeleton-loader v-if="loading" type="list-item-two-line, list-item-two-line"></v-skeleton-loader>

  <v-form v-model="isValid" v-else ref="form">
    <v-row>
      <v-col cols="12" sm="12" v-if="!isCustomSenderSelection">
        <v-row>
          <v-col cols="12" :sm="hasInboxes ? 6 : 12" :order="$vuetify.breakpoint.mobile ? 2 : 1">
            <v-text-field
              v-if="inbox"
              :label="$t('components.template.dialog.preview.sender')"
              :value="inbox.name + ' (' + inbox.identifier + ')'"
              hide-selected
              flat
              outlined
              dense
              hide-details="auto"
              disabled
            >
              <template v-slot:append>
                <v-btn icon small @click="clearInboxSender" class="ml-1">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </template>
            </v-text-field>
            <combo-box
              v-else
              dense
              :label="$t('components.template.dialog.preview.sender')"
              :multiple="false"
              :items="senderNames"
              @changeSelection="changeSenderSelection"
              @changeIsValid="changeIsSenderSelectionValid"
              hide-details="auto"
              disabled
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            :order="$vuetify.breakpoint.mobile ? 1 : 2"
            v-if="partnerId && hasInboxes && !channel"
          >
            <v-select
              dense
              outlined
              :label="$t('components.partner.PartnerSettingsForm.inbox.title')"
              v-model="inbox"
              :items="inboxes"
              :loading="loadingInboxSenders"
              :disabled="loading"
              item-text="name"
              item-value="identifier"
              return-object
              @change="selectInboxSender"
              hide-details="auto"
              clearable
              @click:clear="clearInboxSender"
            >
            </v-select>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" sm="12" v-else>
        <slot name="senderSelection" />
      </v-col>
      <v-col cols="12" sm="12">
        <combo-box
          dense
          :label="$t('components.template.dialog.preview.receiver')"
          :items="to"
          :prefill="prefillTo ? prefillTo : to"
          :rules="isEmailInbox ? receiverMailRules : phoneNumberRules"
          @changeSelection="changeReceiverSelection"
          @changeIsValid="changeIsReceiverSelectionValid"
          hide-details="auto"
        />
      </v-col>
    </v-row>

    <v-row v-if="isEmailInbox">
      <v-col cols="12">
        <v-text-field
          dense
          v-model="renderedSubject"
          :rules="textRules"
          outlined
          :label="$t('components.template.dialog.preview.subject')"
          hide-details="auto"
        />
      </v-col>
    </v-row>

    <v-row dense no-gutters>
      <v-col cols="12" v-if="partnerId && portalContext && canCreateDocument && isAttachmentEnabled" class="mt-2">
        <v-row dense v-if="attachments.length > 0">
          <v-col v-for="(a, i) in attachments" :key="a.id || a.name + i" cols="12" sm="6" md="4" lg="3" xl="2">
            <v-card outlined elevation="0" :loading="a.isLoadingInternal">
              <v-row dense align="center" no-gutters>
                <v-col cols="auto" class="mr-2">
                  <v-icon size="32" color="red darken-1">mdi-file-pdf-box</v-icon>
                </v-col>
                <v-col v-if="a.isUploadError">
                  <span class="text-caption grey--text text-truncate d-block">
                    {{ $t("components.template.dialog.preview.uploadError", { fileName: a.name }) }}
                  </span>
                  <v-btn @click="a.create()" x-small elevation="0" color="warning">{{
                    $t("components.template.dialog.preview.reupload")
                  }}</v-btn>
                </v-col>
                <v-col class="text-truncate" v-else>
                  <span class="text-caption grey--text text-truncate d-block">
                    {{ a.name }}
                  </span>
                  <span class="text-caption grey--text d-block">
                    {{ a.size | formatBytes }}
                  </span>
                </v-col>
                <v-col cols="auto">
                  <v-btn icon x-small @click="removeAttachment(i)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
      <v-col
        cols="12"
        class="d-flex align-center mt-2"
        v-if="partnerId && portalContext && canCreateDocument && isAttachmentEnabled && isEmailInbox"
      >
        <v-menu v-model="attachmentMenu" offset-y left :close-on-content-click="true">
          <template v-slot:activator="{ on, attrs }">
            <v-btn text small class="px-0" v-bind="attrs" v-on="on">
              <v-icon small class="mr-1">mdi-paperclip</v-icon>
              {{ $t("components.template.dialog.preview.addAttachment") }}
            </v-btn>
          </template>

          <v-list dense>
            <v-list-item @click="onPickLocalFile">
              <v-list-item-title>{{ $t("components.template.dialog.preview.browseThisComputer") }}</v-list-item-title>
            </v-list-item>

            <v-list-item @click="documentSelectDialog = true">
              <v-list-item-title>
                {{ $t("components.template.dialog.preview.selectFileFromPlatform") }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <input
          ref="localFileInput"
          type="file"
          multiple
          style="display: none"
          accept="application/pdf,.pdf"
          @change="onLocalFilesSelected"
        />

        <document-selection-simple-dialog
          :isDialogActive.sync="documentSelectDialog"
          :filters="attachmentFileFilters"
          :selectedItems.sync="selected"
          @selected="attachDocuments"
          :key="'document-selection-dialog-' + refKey"
        />
      </v-col>

      <v-col cols="12">
        <template-editor
          :isPlain="isPlain"
          v-model="renderedBody"
          :readOnly="false"
          :partnerId="partnerId"
          :isMobile="isMobile"
          :key="k"
          :label="$t('components.template.detail.body.body')"
        >
          <span v-if="hasTokens">
            <v-btn
              @click="customTokenDialog = true"
              text
              small
              :icon="isMobile"
              :class="isMobile ? 'mr-n1 ml-n1' : 'pr-0 pl-0'"
              :color="isTokenWithoutValue ? 'error' : ''"
            >
              <small v-if="!isMobile" class="mr-1 ml-1">
                {{ $t("components.template.dialog.preview.customTokens") }}
              </small>
              <v-icon v-if="!isMobile" :color="isTokenWithoutValue ? 'error' : ''"> mdi-list-status </v-icon>
              <tooltip v-else :text="$t('components.template.dialog.preview.missingTokens')" class="mr-n1 ml-n1">
                <v-icon small :color="isTokenWithoutValue ? 'error' : ''"> mdi-list-status </v-icon>
              </tooltip>
            </v-btn>
            <template-editor-action-divider />
          </span>
          <span v-if="canReadTemplate">
            <template-selection-dialog
              :partnerId="partnerId"
              :context="extendedContext"
              @select="setTemplate"
              :isPlain="isPlain"
            />
            <template-editor-action-divider v-if="!isPlain" />
          </span>
        </template-editor>
      </v-col>
    </v-row>

    <v-row class="mb-n5 mt-n6" v-if="problems.length > 0">
      <v-col cols="12">
        <small v-for="problem of problems" :key="problem">{{ problem }}</small>
      </v-col>
    </v-row>

    <div v-if="customTokenDialog">
      <confirm-action-dialog
        :title="$t('components.template.dialog.preview.customTokens')"
        :isDialogActive.sync="customTokenDialog"
        :hideLeft="true"
        @rightClick="customTokenDialog = false"
      >
        <div v-for="token of template.content.tokens" :key="token">
          <v-text-field
            :value="getValue(token)"
            :label="getLabel(token)"
            @input="(v) => debounceInput(v, token)"
            outlined
          />
        </div>
      </confirm-action-dialog>
    </div>

    <div v-if="isTicketEnabled && canCreateTicket">
      <ticket-create-checkbox v-model="reminderDate" :enabled.sync="createReminder" />
    </div>
    <debug>
      {{ extendedContext }}
    </debug>
  </v-form>
</template>

<script lang="ts">
import { TemplateTokenConfig } from "@/lib/configuration/template-token.configuration";
import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { ConversationChannelEnum } from "@/lib/enum/conversation-channel.enum";
import { LanguageCodeEnum } from "@/lib/enum/language-code.enum";
import { MessageContentFormatEnum } from "@/lib/enum/templateEnums/messageContentFormat.enum";
import { MessageMedium } from "@/lib/enum/templateEnums/messageMedium.enum";
import { ITemplateContext, ITemplateExtendedContext } from "@/lib/interfaces/template/templateContext.interface";
import { getNestedObjectValues, setNestedObjectValues } from "@/lib/objectPath-helper";
import { emailRule, phoneInternationalRule } from "@/lib/rules/contactRule";
import { requiredRule } from "@/lib/rules/requiredRule";
import { renderTemplate } from "@/lib/utility/renderTemplate";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import { IPageFilterElement } from "@/models/page-filter-element.entity";
import { PartnerMessage } from "@/models/partner-message.entity";
import { IPhoneMessage, PhoneMessage } from "@/models/phone-message.entity";
import { PartnerEntity } from "@/models/partnerEntity";
import { IReference } from "@/models/reference.entity";
import { ISignDocument, SignDocument } from "@/models/sign-document.entity";
import { Ticket } from "@/models/ticket.entity";
import { IUser } from "@/models/user.entity";
import {
  MrfiktivAdminTemplateViewModelGen,
  MrfiktivCreateMessageDtoGen,
  MrfiktivInboxDtoGen,
  MrfiktivMessageAttachmentGen,
  MrfiktivPartnerTemplateViewModelGen,
  MrfiktivSettingPartnerInternalViewModelGen,
  MrfiktivSettingViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ThgAdminTemplateViewModelGen, ThgPartnerTemplateViewModelGen } from "@/services/thg/v1/data-contracts";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { ConfigModule } from "@/store/modules/config";
import { UserModule } from "@/store/modules/me-user.store";
import { SettingModule } from "@/store/modules/setting.store";
import DocumentSelectionSimpleDialog from "@/views/sign/DocumentSelectionSimpleDialog.vue";
import { debounce } from "debounce";
import { mixins } from "vue-class-component";
import { Component, Prop, Watch } from "vue-property-decorator";
import Card from "../utility/Card.vue";
import ComboBox from "../utility/ComboBox.vue";
import ConfirmActionDialog from "../utility/ConfirmActionDialog.vue";
import Debug from "../utility/Debug.vue";
import Tooltip from "../utility/tooltip.vue";
import TemplateEditor from "./TemplateEditor.vue";
import TemplateEditorActionDivider from "./TemplateEditorActionDivider.vue";
import TemplateSelectionDialog from "./TemplateSelectionDialog.vue";
import TicketCreateCheckbox from "./TicketCreateCheckbox.vue";
import { formatBytes } from "@/lib/utility/file-helper";
import { $t } from "@/lib/utility/t";
import { PartnerModule } from "@/store/modules/partner";
import { handleError } from "@/lib/utility/handleError";

@Component({
  components: {
    Card,
    ComboBox,
    Debug,
    ConfirmActionDialog,
    DocumentSelectionSimpleDialog,
    TemplateEditor,
    TemplateSelectionDialog,
    TemplateEditorActionDivider,
    Tooltip,
    TicketCreateCheckbox
  },
  filters: { formatBytes }
})
export default class TemplateCard extends mixins(PermissionMixin) {
  readonly MessageContentFormatEnum = MessageContentFormatEnum;

  /**
   * 8 MB max total attachment size
   */
  private readonly MAX_TOTAL_ATTACHMENT_BYTES = 8 * 1024 * 1024;

  @Prop()
  partnerId?: string;

  @Prop()
  from!: PartnerEntity[];

  @Prop()
  inboxProp?: MrfiktivInboxDtoGen;

  @Prop()
  channel?: ConversationChannelEnum;

  @Prop({ default: true })
  isTicketEnabled?: boolean;

  @Prop()
  context!: ITemplateContext;

  @Prop()
  loadContext?: () => Promise<ITemplateContext>;

  @Prop()
  to!: string[];

  // If undefined, all "to"s are prefilled
  @Prop()
  prefillTo?: string[];

  @Prop({ default: false })
  isCustomSenderSelection?: boolean;

  @Prop()
  isEditorMobile?: boolean;

  @Prop()
  refs!: IReference[];

  @Prop({ type: Boolean, default: true })
  isAttachmentEnabled!: boolean;

  @Prop({ default: () => [] })
  attachmentFileFilters!: IPageFilterElement[];

  attachmentMenu = false;

  get isMobile() {
    if (this.isEditorMobile) {
      return true;
    }
    if (this.$vuetify.breakpoint.mdAndDown) {
      return true;
    }
    return false;
  }

  get refKey(): string | undefined {
    if (this.refs && this.refs.length > 0) {
      return this.refs[0].refId;
    }

    return undefined;
  }

  get portalContext() {
    return ConfigModule.appContext === AppContextEnum.REPORT_PORTAL;
  }

  template: ThgAdminTemplateViewModelGen | ThgPartnerTemplateViewModelGen = {
    key: "",
    content: {
      subject: "",
      body: "",
      tokens: []
    },
    meta: {
      description: "",
      categories: [],
      editor: "tiptap",
      contentFormat: "html",
      language: LanguageCodeEnum.DE,
      title: ""
    },
    timestamp: { created: new Date().toString(), lastModified: new Date().toString() },
    isAdminTemplate: false
  };

  reminderDate: string | null = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  createReminder = false;

  customTokenDialog = false;
  documentSelectDialog = false;

  selectedReceivers: string[] = this.to;
  selectedSender = this.from?.length > 0 ? this.from[0].companyName : "";

  inbox: MrfiktivInboxDtoGen | undefined = this.inboxProp;

  inboxSenders: MrfiktivInboxDtoGen[] = [];
  loadingInboxSenders = false;

  renderedBody = "";
  renderedSubject = "";

  loading = false;

  isValid = false;
  isReceiverSelectionValid = false;
  isSenderSelectionValid = false;

  problems: string[] = [];

  extendedContext: ITemplateExtendedContext = {};

  attachments: ISignDocument[] = [];
  selected: ISignDocument[] = [];

  /**
   * this key is incremented whenever the template is regenerated.
   * it forces the template editor to rerender to be responsive to changes
   */
  k = 0;

  /**
   * How long to wait for changes before search input is updated
   */
  filterDebounceTimeout = 300;

  get isTokenWithoutValue() {
    for (const token of this.template.content.tokens) {
      if (!this.getValue(token)) {
        return true;
      }
    }
    return false;
  }

  get hasTokens() {
    return this.template.content.tokens.length;
  }

  get references() {
    return this.refs || [];
  }

  get templateEditorOffset() {
    if (this.$vuetify.breakpoint.xsOnly) {
      return 190;
    }
    return 150;
  }

  get receiverMailRules() {
    return [emailRule()];
  }

  get phoneNumberRules() {
    return [phoneInternationalRule()];
  }

  get textRules(): any {
    return [requiredRule()];
  }

  get allFormsValid() {
    return this.isValid && this.isReceiverSelectionValid;
  }

  get senderNames() {
    return this.from?.map((s) => s.companyName);
  }

  get selectedSenderId() {
    if (this.inbox) {
      return this.inbox.identifier || this.partnerId || "";
    }

    const sender = this.from?.find((s) => s.companyName === this.selectedSender);

    return sender?._id || "";
  }

  get settingTokens() {
    return this.template.content.tokens.filter((c: string) => c.split(".")[0] === TemplateTokenConfig.settingCategory);
  }

  get customTokens() {
    return this.template.content.tokens.filter((c: string) => c.split(".")[0] === TemplateTokenConfig.customCategory);
  }

  get inboxes() {
    return PartnerModule.partner.settings?.inboxes;
  }

  get hasInboxes() {
    return PartnerModule.partner.settings?.inboxes.length ?? 0;
  }

  get isEmailInbox() {
    if (this.channel) {
      return this.channel === ConversationChannelEnum.EMAIL;
    }
    return !this.inbox || this.inbox?.source === "email";
  }

  get isPlain() {
    return this.template.meta.contentFormat === MessageContentFormatEnum.PLAIN || !this.isEmailInbox;
  }

  async mounted() {
    this.loading = true;

    if (this.channel && this.inboxes) {
      this.inbox = this.inboxes.find((i) => i.source === this.channel);
    } else {
      this.inbox = this.inboxes ? this.inboxes[0] : undefined;
    }

    // for some reason this.template was undefined when mounting
    this.template = {
      key: "",
      content: {
        subject: "",
        body: "",
        tokens: []
      },
      meta: {
        description: "",
        categories: [],
        editor: "tiptap",
        contentFormat: "html",
        language: LanguageCodeEnum.DE,
        title: ""
      },
      timestamp: { created: new Date().toString(), lastModified: new Date().toString() },
      isAdminTemplate: false
    };

    await this.setContext();
    this.generateTemplate();

    this.loading = false;
  }

  @Watch("isValid")
  @Watch("renderedBody")
  @Watch("selectedReceivers")
  isValidWatcher() {
    this.$emit("isValid", this.isValid && this.renderedBody.length > 0 && this.selectedReceivers?.length > 0);
  }

  getLabel(tokenPath: string) {
    const translationPrefix = "components.template.editor.placeholder.path";
    const translation = this.$t(`${translationPrefix}.${tokenPath}`).toString();
    if (translation.startsWith(translationPrefix)) {
      return tokenPath;
    }
    return translation;
  }

  getValue(tokenPath: string) {
    return getNestedObjectValues(this.extendedContext, tokenPath);
  }

  setValue(tokenPath: string, value: string) {
    return setNestedObjectValues(this.extendedContext, tokenPath, value);
  }

  /**
   * Sets the template to loading
   *
   * @param loading
   */
  setLoading(loading: boolean) {
    this.loading = loading;
  }

  changeReceiverSelection(selectedReceivers: string[]) {
    this.selectedReceivers = selectedReceivers;
  }

  changeIsReceiverSelectionValid(isValid: boolean) {
    this.isReceiverSelectionValid = isValid;
  }

  changeSenderSelection(selectedSender: string) {
    this.selectedSender = selectedSender ? selectedSender : "";
  }

  changeIsSenderSelectionValid(isValid: boolean) {
    this.isSenderSelectionValid = isValid;
  }

  debounceInput = debounce(
    (v: any, customTokenKey: string) => this.handleCustomContextChange(v, customTokenKey),
    this.filterDebounceTimeout,
    false
  );

  get my(): IUser {
    return UserModule.user;
  }

  get myUserId() {
    return UserModule.userId;
  }

  async setContext() {
    const context = (await this.getContext()) || {};
    this.extendedContext = { ...context, custom: {}, my: this.my };
  }

  async getContext() {
    const context = this.context;
    if (context) {
      return context;
    }

    const loadContext = this.loadContext;
    if (loadContext) {
      try {
        const context = await loadContext();

        return context;
      } catch (e) {
        this.$log.error(e);
        this.$toast.error((e as any).message);
      }
    }
    this.$log.error("either context or load context must be given");
  }

  /**
   * Add a key value to the extended context manually from outside
   * @param key
   * @param value
   */
  extendCustomExtendedContext(key: string, value: string) {
    // this.extendedContext = { ...this.extendedContext, custom: { ...this.extendedContext.custom, [key]: value } };
    this.debounceInput(value, key);
  }

  async buildExtendedContext() {
    this.$log.debug("EXTENDED CONTEXT CHANGED");
    this.addCustomToContext();
    this.$log.debug("ADDED CUSTOM TOKENS");
    await this.addSettingsToContext();
    this.$log.debug("ADDED SETTINGS TO CONTEXT");
  }

  async loadSetting(key: string) {
    let setting: MrfiktivSettingViewModelGen | MrfiktivSettingPartnerInternalViewModelGen | undefined;

    if (this.partnerId) {
      setting = await SettingModule.getSettingByKey({ key: key, partnerId: this.partnerId });

      if (!setting) {
        this.problems.push(`Setting ${key} not found`);
      }
      if (setting?.isEncrypted) {
        this.problems.push(`Setting ${key} encrypted`);
      }
    } else {
      try {
        setting = await SettingModule.getAdminSettingByKey(key);
        if (setting?.isEncrypted) {
          this.problems.push(`Setting ${key} encrypted`);
        }
      } catch (e) {
        this.problems.push(`Setting ${key} not found`);
      }
    }

    return setting?.value;
  }

  handleCustomContextChange(v: any, customTokenKey: string) {
    this.setValue(customTokenKey, v);

    this.generateTemplate();
  }

  addCustomToContext() {
    const settingContext: Record<string, string> = {};

    this.customTokens.forEach((t) => {
      const key = t.split(".")[1];
      settingContext[key] = `${key}`;
    });

    this.extendedContext.custom = settingContext;
  }

  async addSettingsToContext() {
    const settingsAsync: Promise<{ key: string; value: string | undefined }>[] = this.settingTokens
      .map((st) => st.split(".")[1])
      .map(async (key) => {
        return {
          key: key,
          value: await this.loadSetting(key)
        };
      });

    const settings = await Promise.all(settingsAsync);

    const settingContext: Record<string, string> = {};
    settings.forEach((s) => (settingContext[s.key] = s.value || ""));

    this.extendedContext.setting = settingContext;
  }

  generateTemplate() {
    const input = this.extendedContext;

    this.renderedSubject = renderTemplate(this.template.content.subject, input);
    this.renderedBody = renderTemplate(this.template.content.body, input);

    this.k++;
  }

  setExtendedContext(extendedContext: ITemplateExtendedContext) {
    this.extendedContext = extendedContext;
    this.generateTemplate();
  }

  async setTemplate(template: MrfiktivPartnerTemplateViewModelGen | MrfiktivAdminTemplateViewModelGen) {
    this.template = template;
    await this.buildExtendedContext();
    this.generateTemplate();
  }

  assertMailInformation() {
    const errors: string[] = [];
    const missingErrors: string[] = [];
    if (!this.selectedSenderId) {
      missingErrors.push(this.$t("components.template.detail.errors.sender").toString());
    }
    if (!this.selectedReceivers?.length) {
      missingErrors.push(this.$t("components.template.detail.errors.receiver").toString());
    }
    if (!this.renderedSubject) {
      missingErrors.push(this.$t("components.template.detail.errors.subject").toString());
    }
    if (!this.renderedBody) {
      missingErrors.push(this.$t("components.template.detail.errors.body").toString());
    }
    if (this.attachments && this.attachments.length && this.attachments.some((a) => a.isLoadingInternal)) {
      missingErrors.push(this.$t("components.template.detail.errors.attachment").toString());
    }
    if (missingErrors.length) {
      errors.push(missingErrors.join(", ") + " " + this.$t("components.template.detail.errors.missing").toString());
    }

    if (this.selectedReceivers.length)
      for (const receiver of this.selectedReceivers) {
        const rule = emailRule()(receiver);
        if (rule && rule !== true) {
          errors.push(rule);
        }
      }

    if (errors.length) {
      throw new Error(errors.join(", "));
    }
  }

  assertSmsInformation() {
    const errors: string[] = [];
    const missingErrors: string[] = [];

    if (!this.selectedReceivers?.length) {
      missingErrors.push(this.$t("components.template.detail.errors.receiver").toString());
    }
    if (!this.renderedBody) {
      missingErrors.push(this.$t("components.template.detail.errors.body").toString());
    }

    if (missingErrors.length) {
      errors.push(missingErrors.join(", ") + " " + this.$t("components.template.detail.errors.missing").toString());
    }

    // Validate phone numbers (E.164 format with optional whitespaces)
    if (this.selectedReceivers.length) {
      for (const receiver of this.selectedReceivers) {
        const phoneRegex = /^\+[1-9][\d\s]{1,14}$/;
        if (!phoneRegex.test(receiver)) {
          errors.push(this.$t("components.template.detail.errors.invalidPhoneNumber", { number: receiver }).toString());
        }
      }
    }

    if (errors.length) {
      throw new Error(errors.join(", "));
    }
  }

  async send() {
    if (this.isEmailInbox) {
      return await this.sendMail();
    }

    return await this.sendSms();
  }

  validate() {
    return (this.$refs.form as HTMLFormElement).validate();
  }

  async sendSms() {
    try {
      this.assertSmsInformation();
      this.loading = true;

      const phoneMessages: IPhoneMessage[] = [];

      for (const receiver of this.selectedReceivers) {
        const phoneMessage = PhoneMessage.fromSmsDto(
          PartnerModule.partner.id,
          {
            identifier: this.inbox?.identifier || "",
            to: receiver,
            body: this.renderedBody
          },
          this.refs
        );

        const sentMessage = await phoneMessage.sendSms();
        phoneMessages.push(sentMessage);
      }

      if (this.isTicketEnabled && this.canCreateTicket && this.createReminder) {
        const ticketRefs = this.refs ? [...this.refs] : [];
        for (const phoneMessage of phoneMessages) {
          ticketRefs.push({ refId: phoneMessage.id, refType: BackendResourceEnum.MESSAGE });
        }

        const ticket = new Ticket({
          partnerId: PartnerModule.partner.id,
          title: this.renderedBody.slice(0, 100), // Use first 100 chars of body as title
          refs: ticketRefs,
          assignees: [this.myUserId]
        });

        ticket.due = this.reminderDate ? new Date(this.reminderDate).toISOString() : undefined;

        await ticket.create();
      }

      this.$toast.success(this.$t("components.template.dialog.preview.smsSentConfirmation"));

      // Clear the form
      this.template.content.body = "";
      this.generateTemplate();

      this.$emit("sent", phoneMessages);

      return phoneMessages;
    } catch (e) {
      handleError(e);
    } finally {
      this.loading = false;
    }
  }

  async sendMail() {
    try {
      this.assertMailInformation();
      this.loading = true;
      const mrfiktivCreateMessageDtoGen: MrfiktivCreateMessageDtoGen[] = [];

      const attachments: MrfiktivMessageAttachmentGen[] | undefined = this.attachments.length
        ? this.attachments.map((a) => ({
            name: a.name,
            type: "application/pdf",
            size: a.size || 0,
            ref: {
              refId: a.id || "",
              refType: ResourceEnum.DOCUMENT
            }
          }))
        : undefined;

      for (const receiver of this.selectedReceivers) {
        const messageDto: MrfiktivCreateMessageDtoGen = {
          to: receiver,
          content: {
            subject: this.renderedSubject,
            body: this.renderedBody
          },
          attachments: attachments,
          meta: {
            contentFormat: MessageContentFormatEnum.HTML,
            medium: MessageMedium.EMAIL
          }
        };

        mrfiktivCreateMessageDtoGen.push(messageDto);
      }

      const promises = [];
      for (const dto of mrfiktivCreateMessageDtoGen) {
        promises.push(new PartnerMessage({ partnerId: this.partnerId, ...dto, refs: this.refs ?? [] }).create());
      }
      const messages = await Promise.all(promises);

      if (this.isTicketEnabled && this.canCreateTicket && this.createReminder) {
        const ticketRefs = this.refs ? [...this.refs] : [];
        for (const message of messages) {
          ticketRefs.push({ refId: message.id, refType: ResourceEnum.MESSAGE });
        }

        const ticket = new Ticket({
          partnerId: this.partnerId,
          title: this.renderedSubject,
          refs: ticketRefs,
          assignees: [this.myUserId]
        });

        ticket.due = this.reminderDate ? new Date(this.reminderDate).toISOString() : undefined;

        await ticket.create();
      }

      this.$toast.success(this.$t("components.template.dialog.preview.sentConfirmation"));

      this.template.content.subject = "";
      this.template.content.body = "";
      this.attachments = [];
      this.generateTemplate();

      this.$emit("sent", messages);

      return messages;
    } catch (e) {
      handleError(e);
    } finally {
      this.loading = false;
    }
  }

  onPickLocalFile() {
    this.attachmentMenu = false;
    (this.$refs.localFileInput as HTMLInputElement).click();
  }

  async onLocalFilesSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    if (!input.files) return;

    const files = Array.from(input.files);
    const newTotal = files.reduce((sum, f) => sum + f.size, 0);

    if (!this.canAddAttachments(newTotal)) {
      input.value = "";
      return;
    }

    const newItems = files.map(
      (f) =>
        new SignDocument({
          name: f.name,
          title: f.name,
          isTemplate: false,
          folder: "/attachments/",
          refs: this.refs,
          file: f,
          partnerId: this.partnerId
        })
    );
    this.attachments.push(...newItems);

    for (const item of newItems) {
      item.create();
    }

    input.value = "";
  }

  getTotalAttachmentBytes() {
    return this.attachments.reduce((sum, a) => sum + (a.size || 0), 0);
  }

  removeAttachment(i: number) {
    this.attachments.splice(i, 1);
  }

  /**
   * Check if new attachments can be added without exceeding max total size
   * @param newBytes
   */
  canAddAttachments(newBytes: number): boolean {
    const currentTotal = this.getTotalAttachmentBytes();
    const maxMb = (this.MAX_TOTAL_ATTACHMENT_BYTES / (1024 * 1024)).toFixed(0);

    if (currentTotal + newBytes > this.MAX_TOTAL_ATTACHMENT_BYTES) {
      this.$toast.error($t("components.template.detail.errors.toLarge", { maxMb }).toString());
      return false;
    }

    return true;
  }

  attachDocuments(documents: ISignDocument[]) {
    const invalidDocs = documents.filter((d) => !d.id);
    if (invalidDocs.length > 0) {
      this.$toast.error($t("components.template.detail.errors.missingId").toString());
      return;
    }

    const existingIds = new Set(this.attachments.map((a) => a.id).filter((id): id is string => !!id));

    const uniqueNewDocs: ISignDocument[] = [];
    let droppedCount = 0;

    for (const d of documents) {
      if (!d.id) {
        this.$toast.error($t("components.template.detail.errors.missingId").toString());
        return;
      }
      if (existingIds.has(d.id)) {
        droppedCount++;
        continue;
      }
      uniqueNewDocs.push(d);
    }

    if (droppedCount > 0) {
      this.$toast.info($t("components.template.detail.errors.duplicate", { droppedCount }).toString());
    }

    const newTotal = uniqueNewDocs.reduce((sum, d) => sum + (d.size || 0), 0);

    if (!this.canAddAttachments(newTotal)) {
      return;
    }

    this.attachments.push(...uniqueNewDocs);
    this.selected = [];
  }

  selectInboxSender(inbox: MrfiktivInboxDtoGen) {
    if (inbox) {
      this.inbox = inbox;
      this.selectedSender = ""; // Clear regular sender selection
      this.$emit("senderChanged", inbox);
    }
  }

  clearInboxSender() {
    this.inbox = undefined;
    if (this.from?.length > 0) {
      this.selectedSender = this.from[0].companyName;
    }
    this.$emit("senderChanged", null);
  }
}
</script>

<style scoped>
.preview {
  width: 100%;
  border-radius: 5px;
  border-style: solid;
  border-width: 1px;
  border-color: lightgrey;
  height: calc(100% - 52px);
}
</style>
